import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  User,
  Target,
  HelpCircle,
  Loader2,
  Megaphone,
  DollarSign,
  MousePointerClick,
  Eye,
  Star,
  MapPin,
  Search,
  TrendingUp
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { DashboardStats, Project, Transaction, FinanceStats, ClientMarketingSummary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';

interface ChartDataPoint {
  date: string;
  value: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { isInternalMode, mode, selectedClientId, selectedClient, clients } = useClient();
  const navigate = useNavigate();

  // Redirect to leadgen dashboard when in leadgen mode
  useEffect(() => {
    if (mode === 'leadgen') {
      navigate('/leadgen');
    }
  }, [mode, navigate]);

  // Internal mode state
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [financeStats, setFinanceStats] = useState<FinanceStats | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectChartData, setProjectChartData] = useState<ChartDataPoint[]>([]);
  const [revenueChartData, setRevenueChartData] = useState<ChartDataPoint[]>([]);

  // Client mode state
  const [marketingSummary, setMarketingSummary] = useState<ClientMarketingSummary | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const generateDateRange = (days: number): string[] => {
    const dates: string[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    return dates;
  };

  const loadInternalData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsData, projectsData, transactionsData, financeData] = await Promise.all([
        api.getDashboardStats(),
        api.getProjects({ status: 'active' }),
        api.getTransactions(),
        api.getFinanceStats()
      ]);
      setStats(statsData);
      setFinanceStats(financeData);
      setProjects(projectsData.results.slice(0, 10));

      const allProjects = await api.getProjects();
      const dates = generateDateRange(30);
      const projectCounts: Record<string, number> = {};
      dates.forEach(date => { projectCounts[date] = 0; });

      allProjects.results.forEach((project: Project) => {
        const createdDate = new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (projectCounts[createdDate] !== undefined) {
          projectCounts[createdDate]++;
        }
      });

      setProjectChartData(dates.map(date => ({ date, value: projectCounts[date] || 0 })));

      const revenueCounts: Record<string, number> = {};
      dates.forEach(date => { revenueCounts[date] = 0; });

      transactionsData.results.forEach((transaction: Transaction) => {
        if (transaction.transaction_type === 'income' && transaction.status === 'completed') {
          const transDate = new Date(transaction.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (revenueCounts[transDate] !== undefined) {
            revenueCounts[transDate] += parseFloat(transaction.amount);
          }
        }
      });

      setRevenueChartData(dates.map(date => ({ date, value: revenueCounts[date] || 0 })));
    } catch (error) {
      console.error('Failed to load internal dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadClientData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (selectedClientId) {
        // Load specific client's marketing data
        const summary = await api.getClientMarketingSummary(selectedClientId);
        setMarketingSummary(summary);
      } else {
        // Load aggregated marketing data for all clients
        // For now, show a message to select a client
        setMarketingSummary(null);
      }
    } catch (error) {
      console.error('Failed to load client dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (isInternalMode) {
      loadInternalData();
    } else {
      loadClientData();
    }
  }, [isInternalMode, loadInternalData, loadClientData]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'paused': return 'status-badge status-paused';
      case 'completed': return 'status-badge status-completed';
      default: return 'status-badge status-completed';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '$0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${(num || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  // CLIENT MODE DASHBOARD
  if (!isInternalMode) {
    return (
      <div className="p-3">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-3">
            <div className="flex items-baseline gap-3">
              <h1 className="text-base font-semibold text-gray-900 dark:text-white tracking-wide">
                {selectedClient ? selectedClient.company : 'MARKETING DASHBOARD'}
              </h1>
              <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate()}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {selectedClient ? 'Marketing performance overview' : 'Select a client to view their marketing data'}
            </p>
          </div>

          {!selectedClientId ? (
            // No client selected - show client selection prompt
            <div className="panel p-6 text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Select a Client</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Choose a client from the dropdown above to view their marketing performance
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {clients.slice(0, 5).map(client => (
                  <span key={client.id} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400">
                    {client.company}
                  </span>
                ))}
                {clients.length > 5 && (
                  <span className="px-2 py-1 text-xs text-gray-500">+{clients.length - 5} more</span>
                )}
              </div>
            </div>
          ) : marketingSummary ? (
            // Client selected - show marketing metrics
            <>
              {/* Marketing Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
                <Link to="/google-ads" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-blue-500" />
                    <span className="stat-label">Ad Spend</span>
                  </div>
                  <p className="stat-value">{formatCurrency(marketingSummary.google_ads.total_spend)}</p>
                </Link>

                <Link to="/google-ads" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <MousePointerClick className="w-4 h-4 text-purple-500" />
                    <span className="stat-label">Ad Clicks</span>
                  </div>
                  <p className="stat-value">{formatNumber(marketingSummary.google_ads.total_clicks)}</p>
                </Link>

                <Link to="/google-ads" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-green-500" />
                    <span className="stat-label">Conversions</span>
                  </div>
                  <p className="stat-value">{formatNumber(marketingSummary.google_ads.total_conversions)}</p>
                </Link>

                <Link to="/gbp" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="stat-label">GBP Rating</span>
                  </div>
                  <p className="stat-value">{marketingSummary.gbp.avg_rating.toFixed(1)}</p>
                </Link>

                <Link to="/gbp" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-blue-500" />
                    <span className="stat-label">GBP Views</span>
                  </div>
                  <p className="stat-value">{formatNumber(marketingSummary.gbp.total_views)}</p>
                </Link>

                <Link to="/seo" className="stat-card hover:border-primary-300 dark:hover:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Search className="w-4 h-4 text-green-500" />
                    <span className="stat-label">SEO Clicks</span>
                  </div>
                  <p className="stat-value">{formatNumber(marketingSummary.gsc.total_clicks)}</p>
                </Link>
              </div>

              {/* Service Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {/* Google Ads Card */}
                <Link to="/google-ads" className="panel p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Google Ads</h3>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Accounts</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.google_ads.accounts_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Spend</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(marketingSummary.google_ads.total_spend)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Clicks</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.google_ads.total_clicks)}</span>
                    </div>
                  </div>
                </Link>

                {/* GBP Card */}
                <Link to="/gbp" className="panel p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Google Business</h3>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Locations</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gbp.locations_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Reviews</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gbp.total_reviews)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Avg Rating</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gbp.avg_rating.toFixed(1)}</span>
                    </div>
                  </div>
                </Link>

                {/* SEO Card */}
                <Link to="/seo" className="panel p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">SEO</h3>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Properties</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gsc.properties_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Clicks</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gsc.total_clicks)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Impressions</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gsc.total_impressions)}</span>
                    </div>
                  </div>
                </Link>
              </div>
            </>
          ) : (
            <div className="panel p-6 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">No marketing data available for this client</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // INTERNAL MODE DASHBOARD
  return (
    <div className="p-3">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-3">
          <div className="flex items-baseline gap-3">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white tracking-wide">DASHBOARD</h1>
            <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate()}</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {getGreeting()}, {user?.first_name || user?.username || 'there'}
          </p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
          <Link to="/projects" className="stat-card hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.projects.active || 0}</p>
                <p className="stat-label">Active Projects</p>
              </div>
              <Calendar className="w-5 h-5 text-primary-500 flex-shrink-0" />
            </div>
          </Link>

          <Link to="/pipeline" className="stat-card hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.pipeline?.leads || 0}</p>
                <p className="stat-label">New Leads</p>
              </div>
              <Target className="w-5 h-5 text-primary-500 flex-shrink-0" />
            </div>
          </Link>

          <Link to="/outreach" className="stat-card hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.campaigns.active || 0}</p>
                <p className="stat-label">Campaigns</p>
              </div>
              <Megaphone className="w-5 h-5 text-primary-500 flex-shrink-0" />
            </div>
          </Link>

          <Link to="/clients" className="stat-card hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.clients.total || 0}</p>
                <p className="stat-label">Total Clients</p>
              </div>
              <User className="w-5 h-5 text-primary-500 flex-shrink-0" />
            </div>
          </Link>

          <Link to="/finance" className="stat-card hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">${financeStats?.total_revenue.toLocaleString() || 0}</p>
                <p className="stat-label">Revenue</p>
              </div>
              <HelpCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
            </div>
          </Link>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 mb-3">
          <div className="panel p-3">
            <h3 className="section-title mb-2">Projects Created (30d)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={projectChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="value" name="Projects" stroke="#ffc04b" fill="#fffbf0" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="panel p-3">
            <h3 className="section-title mb-2">Revenue (30d)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} stroke="#9ca3af" />
                <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} contentStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="value" name="Revenue" stroke="#000000" fill="#f5f5f5" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Projects Table */}
        <div className="panel">
          <div className="flex items-center justify-between p-2 border-b border-light-border dark:border-dark-border">
            <h3 className="section-title">Recent Projects</h3>
            <Link to="/projects" className="text-xs text-primary-500 hover:text-primary-600 font-medium">
              View All
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">
              No active projects.{' '}
              <Link to="/projects" className="text-primary-500 hover:underline">Create one</Link>
            </div>
          ) : (
            <table className="table-compact">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Progress</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{project.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{project.project_type}</p>
                      </div>
                    </td>
                    <td className="text-gray-700 dark:text-gray-300">{project.client_name}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${project.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-300">{project.progress}%</span>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusClass(project.status)}>{project.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
